import React, { useCallback, useEffect, useState, useMemo } from 'react';

import Card from './components/AgendaContent/Card';
import EmptyAgenda from './components/AgendaContent/EmptyAgenda';
import Filterbar from './components/AgendaFilters/Filterbar';
import Loader from './components/AgendaContent/Loader';

/**
	* @description		Core functionality for filtering the agenda
*/
const Filter = ({
	config,
	eventFeed,
	availableTypes,
	availableCountries,
	rootElement,
}) => {

	const [filteredElements, setFilteredElements] = useState([]); //result of the filtered data
	const [rendering, setRendering] = useState(true);
	const [cards, setCards] = useState([]); //state that holds the rendered components

	//state reflecting params that tweak the filtering
	const [activeType, setActiveType] = useState(null); //object (null = 'all'; object = checkout activeType
	const [activeCountry, setActiveCountry] = useState(null); //object (null = 'all'; object = checkout activeCountry

	// memo's to avoid unnecessary rerender filterbar
	const memoizedActiveType = useMemo(() => activeType, [activeType]);
	const memoizedActiveCountry = useMemo(() => activeCountry, [activeCountry]);

	const updateUrl = useCallback(() => {
		const urlParams = new URLSearchParams();
		if (activeType) urlParams.set('f-type', encodeURI(Object.keys(activeType)[0].toString()));
		if (activeCountry) urlParams.set('f-country', encodeURI(Object.keys(activeCountry)[0].toString()));
		window.history.replaceState(null, '', '?' + urlParams.toString());
	}, [activeType, activeCountry]);

	const setTypeParam = (value) => {
		setActiveType(value);
	};

	const setCountryParam = (value) => {
		setActiveCountry(value);
	};

	/**
	 * @description	Main filtering functionality, activated by a change in params through useEffect below
	 */
   const filterAccordingToCurrentParams = useCallback((input) => {
		let copyInput = structuredClone(input);

		//filtering elements by type
		let resultTypeFilter = [];
		if (activeType) {
			resultTypeFilter = copyInput.filter((element) => Object.keys(element.type)[0] === Object.keys(activeType)[0]);
		} else {
			resultTypeFilter = structuredClone(copyInput);
		}

		//filtering elements by country
		let resultCountryFilter = [];
		if (activeCountry) {
			resultCountryFilter = resultTypeFilter.filter((element) => Object.keys(element.country)[0] === Object.keys(activeCountry)[0]);
		} else {
			resultCountryFilter = structuredClone(resultTypeFilter);
		}

		return resultCountryFilter;
	}, [activeType, activeCountry]);

	/**
	 * @description	Main filter (reacting to a change in params)
	 */
	useEffect(() => {
		if (!eventFeed) return;
		let filterDataObj = structuredClone(eventFeed);
		setFilteredElements(filterAccordingToCurrentParams(filterDataObj));
	}, [eventFeed, activeType, filterAccordingToCurrentParams, activeCountry]);

	/**
	 * @description	Renders the ReactElement cards based on the filteredElements
	 */
	useEffect(() => {
		setRendering(true);
		setCards(filteredElements.map((element, index) =>
			<Card key={index} element={element} />
		));
		setRendering(false);
	}, [activeType, activeCountry, filteredElements]);

	/**
	 * @description	Writes the current parameters to the url
	 */
	useEffect(() => {
		if (!rendering) updateUrl();
	}, [activeType, activeCountry, updateUrl, rendering]);


	/**
	 * @description	Gets the parameters from url and sets the state accordingly
	 */
	useEffect(() => {
		if (eventFeed && window.location.search) {
			const queryParams = new URLSearchParams(window.location.search);

			if (queryParams.has('f-type')) {
				const slugs = decodeURI(queryParams.get('f-type') ?? '').split('+');
				const param = availableTypes.filter((type => slugs.includes(Object.keys(type)[0])))[0];
				if (param) setActiveType(param);
			}

			if (queryParams.has('f-country')) {
				const slugs = decodeURI(queryParams.get('f-country') ?? '').split('+');
				const param = availableCountries.filter((country => slugs.includes(Object.keys(country)[0])))[0];
				if (param) setActiveCountry(param);
			}

		}
	}, [eventFeed]);


	if (rendering) return (
		<div className="loader__container--agenda">
			<Loader />
		</div>
	);

	return (
		<>
			<Filterbar
				memoizedActiveType={memoizedActiveType}
				memoizedActiveCountry={memoizedActiveCountry}

				availableTypes={availableTypes}
				availableCountries={availableCountries}

				setTypeParam={setTypeParam}
				setCountryParam={setCountryParam}

				rootElement={rootElement}
			/>
			{ (cards?.length < 1)? (
				<EmptyAgenda
					text={config.empty_agenda_text}
					subtext={config.empty_agenda_subtext}
				/>
			) : (
				<div className="js_cards-container featured status-cards">
					{cards}
				</div>
			)}
		</>
	);
};

export default React.memo(Filter);
